/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHERE THE API IS — RESOLVED AT RUNTIME, NOT AT BUILD TIME.
 *
 *  Vite inlines `import.meta.env.VITE_*` when the bundle is COMPILED. If the API's
 *  address were a build-time constant, pointing the same image at a different host — a
 *  machine IP today, a proxied hostname tomorrow — would mean rebuilding and
 *  redeploying the frontend. That is the wrong shape for something an operator sets in
 *  `.env`.
 *
 *  So the built image is environment-agnostic and reads its address from a tiny script
 *  the container writes at START-UP, from `API_BASE_URL`:
 *
 *      /runtime-config.js   →   window.__PAMS_CONFIG__ = { apiBaseUrl: '…' }
 *
 *  Build once, configure per deployment.
 *
 *  Precedence, most specific first:
 *    1. `window.__PAMS_CONFIG__.apiBaseUrl`  — written by the container entrypoint
 *    2. `import.meta.env.VITE_API_BASE_URL`  — an escape hatch for `pnpm dev`
 *    3. `/api`                               — same-origin, through the nginx or Vite proxy
 *
 *  The `/api` default is what P0 did, so a deployment that sets nothing behaves exactly
 *  as before.
 *
 *  ⚠️ [amended P5b] STEP 2 OF THAT PRECEDENCE IS THE ONE THING THAT STAYS IN THE APP.
 *
 *  `import.meta.env` is a Vite construct with no meaning in a plain TypeScript package,
 *  and typing it here would make this package depend on `vite/client` — i.e. would make
 *  the shared client layer bundler-specific for one line. So the pure resolution lives
 *  here and each app keeps a three-line `apiBaseUrl()` that supplies its own build-time
 *  value. Both apps read the same `window.__PAMS_CONFIG__`, and both containers write it
 *  from the same `API_BASE_URL` key.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type RuntimeConfig = {
  /**
   * Either an absolute origin (`http://192.168.1.5:5210`) for a direct cross-origin
   * call, or a same-origin path prefix (`/api`) to go through the proxy. Both are
   * legitimate; the proxy form avoids CORS entirely, the absolute form does not need a
   * proxy in front.
   */
  apiBaseUrl: string;
};

declare global {
  interface Window {
    __PAMS_CONFIG__?: Partial<RuntimeConfig>;
  }
}

/** The same-origin proxy path. Used when nothing is configured. */
export const DEFAULT_API_BASE_URL = '/api';

/**
 * A trailing slash here produces `//officers` in every request path, which some proxies
 * normalise and some 404 on. Strip it once, at the boundary.
 */
const stripTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

/**
 * Exported for the test, and deliberately tolerant: an operator who leaves the
 * placeholder in, or writes whitespace, gets the working default rather than an app that
 * fails to boot with a blank screen.
 */
export const resolveApiBaseUrl = (input: { runtime?: string | undefined; buildTime?: string | undefined }): string => {
  for (const candidate of [input.runtime, input.buildTime]) {
    const trimmed = typeof candidate === 'string' ? candidate.trim() : '';
    // `__API_BASE_URL__` is what the entrypoint writes when the variable is unset, so it
    // is treated as "not configured" rather than as a hostname.
    if (trimmed.length > 0 && !trimmed.startsWith('__')) return stripTrailingSlash(trimmed);
  }
  return DEFAULT_API_BASE_URL;
};

/** What the browser currently has, if anything. The build-time half is the app's to supply. */
export const configuredApiBaseUrl = (): string | undefined =>
  typeof window === 'undefined' ? undefined : window.__PAMS_CONFIG__?.apiBaseUrl;
